'use client';

import { FeatherAlertTriangle, FeatherRefreshCw } from '@subframe/core';
import { useState } from 'react';
import { Badge, Button } from '@/shared/components/ui';
import type { RejectedInvoice } from '../types';
import { formatarDataHora, formatarMoedaBR } from '../utils/apuracaoFormatters';
interface ApuracaoEventosRejeitadosTabProps {
  rejectedInvoices: RejectedInvoice[];
  loading: boolean;
  onReemitir: (invoiceId: string) => Promise<void>;
  saving: boolean;
  bloqueado: boolean;
}
export function ApuracaoEventosRejeitadosTab({
  rejectedInvoices,
  loading,
  onReemitir,
  saving,
  bloqueado
}: ApuracaoEventosRejeitadosTabProps) {
  const [reemitindoId, setReemitindoId] = useState<string | null>(null);
  const handleReemitir = async (invoiceId: string) => {
    setReemitindoId(invoiceId);
    try {
      await onReemitir(invoiceId);
    } finally {
      setReemitindoId(null);
    }
  };
  if (loading) {
    return <div className="text-body font-body text-subtext-color rounded-[12px] border border-solid border-neutral-200 px-6 py-12 text-center">
        Carregando faturas rejeitadas…
      </div>;
  }
  if (rejectedInvoices.length === 0) {
    return <div className="bg-success-50 border-success-200 flex flex-col items-center justify-center gap-2 rounded-[16px] border border-solid px-6 py-12 text-center">
        <span className="text-body-bold font-body-bold text-success-800">
          Tudo limpo — nenhuma fatura com erro de emissão neste ciclo
        </span>
        <span className="text-body font-body text-success-700">
          Quando a emissão de uma fatura falhar no Asaas, ela aparecerá aqui para reemissão.
        </span>
      </div>;
  }
  return <div className="flex flex-col gap-4">
      <div className="border-error-200 bg-error-50 flex items-center gap-3 rounded-[12px] border px-4 py-3">
        <FeatherAlertTriangle className="text-error-600 h-5 w-5" />
        <div>
          <p className="text-error-700 text-body-bold font-body-bold">
            {rejectedInvoices.length}{' '}
            {rejectedInvoices.length === 1 ? 'fatura com erro de emissão' : 'faturas com erro de emissão'}
          </p>
          <p className="text-error-700 text-caption font-caption">
            A cobrança não foi gerada no Asaas. Corrija a causa e reemita.
          </p>
        </div>
      </div>

      {bloqueado && <div className="rounded-[8px] border border-solid border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
          Este ciclo está fechado — a reemissão só é possível após reabrir formalmente (somente
          Admin).
        </div>}

      <ul className="flex flex-col gap-3">
        {rejectedInvoices.map(fatura => <li key={fatura.invoiceId} className="bg-default-background flex flex-col gap-3 rounded-[12px] border border-solid border-neutral-200 p-4 md:flex-row md:items-center md:gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="error">Erro de emissão</Badge>
                <span className="text-body-bold font-body-bold text-default-font truncate">
                  Fatura {fatura.number}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {formatarMoedaBR(fatura.totalValue)}
                </span>
              </div>
              <span className="text-caption font-caption text-subtext-color">
                {formatarDataHora(fatura.issuedAt)}
              </span>
              {fatura.asaasError && <p className="text-error-700 text-caption font-caption mt-1">{fatura.asaasError}</p>}
            </div>
            <Button variant="brand-primary" size="medium" icon={<FeatherRefreshCw />} loading={saving && reemitindoId === fatura.invoiceId} disabled={saving || bloqueado} onClick={() => handleReemitir(fatura.invoiceId)}>
              Reemitir
            </Button>
          </li>)}
      </ul>
    </div>;
}